import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { 
  CreditCard, 
  Flag,
  FlagOff,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";

export const SubscriptionsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [toggling, setToggling] = useState(null);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/subscriptions");
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleToggleFlag = async (subId, currentlyFlagged) => {
    setToggling(subId);
    try {
      if (currentlyFlagged) {
        await api.post(`/subscriptions/${subId}/unflag`);
        toast.success("Subscription unmarked");
      } else {
        await api.post(`/subscriptions/${subId}/flag-cancel`);
        toast.success("Subscription marked for review");
      }
      setSubscriptions(prev => prev.map(s => 
        s.id === subId ? { ...s, flagged_for_cancel: !s.flagged_for_cancel } : s
      ));
    } catch (error) {
      toast.error("Failed to update subscription");
    } finally {
      setToggling(null);
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric"
    });
  };

  const totalMonthly = subscriptions.reduce((sum, sub) => {
    if (sub.cadence === "monthly") return sum + sub.amount;
    if (sub.cadence === "yearly") return sum + (sub.amount / 12);
    return sum;
  }, 0);

  const flaggedCount = subscriptions.filter(s => s.flagged_for_cancel).length;
  const potentialSavings = subscriptions
    .filter(s => s.flagged_for_cancel)
    .reduce((sum, sub) => {
      if (sub.cadence === "monthly") return sum + sub.amount;
      if (sub.cadence === "yearly") return sum + (sub.amount / 12);
      return sum;
    }, 0);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="subscriptions-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <div className="space-y-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="subscriptions-page">
      {/* Header */}
      <header>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">Subscriptions</h1>
        <p className="text-muted-foreground mt-1">
          Manage recurring charges and find savings
        </p>
      </header>

      {/* Summary Cards */}
      <section className="grid gap-4 md:grid-cols-3">
        <Card className="border-border/50" data-testid="total-subscriptions-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
            </div>
            <p className="overline">Active Subscriptions</p>
            <p className="metric-value mt-1">{subscriptions.length}</p>
          </CardContent>
        </Card>

        <Card className="border-border/50" data-testid="monthly-cost-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="overline">Monthly Cost</p>
            <p className="metric-value mt-1 tabular-nums">{formatCurrency(totalMonthly)}</p>
          </CardContent>
        </Card>

        <Card className={`border-border/50 ${potentialSavings > 0 ? "border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20" : ""}`} data-testid="potential-savings-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                potentialSavings > 0 
                  ? "bg-emerald-100 dark:bg-emerald-900/30" 
                  : "bg-muted"
              }`}>
                <TrendingUp className={`w-5 h-5 ${
                  potentialSavings > 0 
                    ? "text-emerald-600 dark:text-emerald-400" 
                    : "text-muted-foreground"
                }`} />
              </div>
            </div>
            <p className="overline">Potential Savings</p>
            <p className={`metric-value mt-1 tabular-nums ${
              potentialSavings > 0 ? "text-emerald-600 dark:text-emerald-400" : ""
            }`}>
              {formatCurrency(potentialSavings)}/mo
            </p>
            {flaggedCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">{flaggedCount} marked for review</p>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="empty-state">
            <CreditCard className="empty-state-icon" />
            <h3 className="empty-state-title">No subscriptions detected</h3>
            <p className="empty-state-description">
              We&apos;ll automatically detect recurring charges from your transactions.
            </p>
          </CardContent>
        </Card>
      ) : (
        <section className="space-y-3">
          <h2 className="section-label">All Subscriptions</h2>
          {subscriptions.map((sub) => (
            <Card 
              key={sub.id} 
              className={`border-border/50 transition-all ${
                sub.flagged_for_cancel ? "border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/10" : ""
              }`}
              data-testid={`subscription-${sub.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      sub.flagged_for_cancel 
                        ? "bg-amber-100 dark:bg-amber-900/30" 
                        : "bg-indigo-100 dark:bg-indigo-900/30"
                    }`}>
                      {sub.flagged_for_cancel ? (
                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <CreditCard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{sub.merchant}</span>
                        {sub.flagged_for_cancel && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            Review
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Next: {formatDate(sub.next_charge)}
                        </span>
                        <span className="capitalize">{sub.cadence}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(sub.amount)}
                    </span>
                    <Button
                      variant={sub.flagged_for_cancel ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleFlag(sub.id, sub.flagged_for_cancel)}
                      disabled={toggling === sub.id}
                      className={`min-h-[36px] rounded-full ${
                        sub.flagged_for_cancel 
                          ? "bg-emerald-600 hover:bg-emerald-700" 
                          : ""
                      }`}
                      data-testid={`toggle-flag-${sub.id}`}
                    >
                      {toggling === sub.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : sub.flagged_for_cancel ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Keep
                        </>
                      ) : (
                        <>
                          <Flag className="w-4 h-4 mr-1" />
                          Review
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {/* Tips Section */}
      {flaggedCount > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-700 dark:text-emerald-400">
                  Cancel {flaggedCount} subscription{flaggedCount > 1 ? "s" : ""} to save {formatCurrency(potentialSavings * 12)}/year
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Contact each service provider to cancel. Keep this page as your checklist!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
